import { DashboardType } from "@/types/dashboard/Dashboard";

/** Metas numéricas oficiais (documento BRPoints — Plano de Carreira Associados) */
export const CAREER_THRESHOLDS = {
  /** Nível 1 — empresas cadastradas */
  nivel1Empresas: 50,
  /** Nível 2 — associados na equipe */
  nivel2Associados: 10,
  /** Nível 2 — empresas na rede */
  nivel2EmpresasRede: 500,
  /** Níveis 3 e 4 — empresas ativas na rede */
  nivel3Empresas: 1000,
  /** Marco Gerente — volume transacionado (R$) */
  marcoGerenteFaturamento: 1_000_000,
  /** Marco Diretor — volume transacionado (R$) */
  marcoDiretorFaturamento: 10_000_000,
  /** Marco Associado — plaquinha (R$) */
  marcoAssociadoFaturamento: 50_000,
  /** Marco Gestor — liderança (R$) */
  marcoGestorFaturamento: 500_000,
} as const;

export type CareerRank = 1 | 2 | 3 | 4;

export type CareerLevelDefinition = {
  rank: CareerRank;
  title: string;
  objective: string;
  metas: string[];
  remuneracao: string[];
  recorrencia: string[];
  reconhecimento: string[];
  papel: string[];
};

/** Textos alinhados ao documento oficial (resumo para UI) */
export const CAREER_LEVELS: CareerLevelDefinition[] = [
  {
    rank: 1,
    title: "Associado",
    objective: "Gerar resultado rápido e iniciar renda recorrente.",
    metas: [`${CAREER_THRESHOLDS.nivel1Empresas} empresas cadastradas`],
    remuneracao: ["R$ 80 por adesão (empresas próprias).", "Potencial: R$ 4.000 ao atingir a meta de empresas."],
    recorrencia: [
      "0,10% no Pix e 0,05% no cartão sobre empresas próprias.",
      "Recorrência válida para empresas premium em transação (regra de sustentabilidade).",
    ],
    reconhecimento: [
      "Plaquinha oficial.",
      `Marco: R$ ${(CAREER_THRESHOLDS.marcoAssociadoFaturamento / 1_000).toFixed(0)} mil transacionados.`,
    ],
    papel: ["Prospecção", "Apresentação", "Fechamento"],
  },
  {
    rank: 2,
    title: "Gestor de Associados",
    objective: "Construir equipe e escalar resultados.",
    metas: [
      `${CAREER_THRESHOLDS.nivel2Associados} associados na equipe`,
      `${CAREER_THRESHOLDS.nivel2EmpresasRede} empresas na rede`,
    ],
    remuneracao: [
      "R$ 10 por adesão da equipe (até R$ 5.000).",
      "R$ 80 por adesões próprias.",
    ],
    recorrencia: ["0,20% no Pix e 0,10% no cartão sobre ganhos da equipe."],
    reconhecimento: [
      "Plaquinha de liderança.",
      `Marco: R$ ${(CAREER_THRESHOLDS.marcoGestorFaturamento / 1_000).toFixed(0)} mil transacionados.`,
    ],
    papel: ["Formação de equipe", "Treinamento", "Gestão de performance"],
  },
  {
    rank: 3,
    title: "Gerente de Expansão",
    objective: "Expandir operação e consolidar rede.",
    metas: [
      `${CAREER_THRESHOLDS.nivel3Empresas} empresas ativas`,
      `Marco: R$ ${(CAREER_THRESHOLDS.marcoGerenteFaturamento / 1_000_000).toFixed(0)} milhão transacionados`,
    ],
    remuneracao: [
      "R$ 10 por adesão da equipe (limitado a R$ 10.000).",
      "R$ 80 por adesões próprias.",
    ],
    recorrencia: ["0,30% no Pix e 0,15% no cartão sobre rede ativa."],
    reconhecimento: ["Plaquinha de expansão.", "Marco: R$ 1.000.000 transacionados."],
    papel: ["Expansão regional", "Gestão de rede", "Performance e qualidade"],
  },
  {
    rank: 4,
    title: "Diretor de Expansão",
    objective: "Liderança estratégica e escala de crescimento.",
    metas: [
      `${CAREER_THRESHOLDS.nivel3Empresas}+ empresas ativas`,
      `Marco: R$ ${(CAREER_THRESHOLDS.marcoDiretorFaturamento / 1_000_000).toFixed(0)} milhões transacionados`,
    ],
    remuneracao: ["0,40% no Pix e 0,20% no cartão sobre toda a operação."],
    recorrencia: ["Recorrência alinhada à rede premium em atividade (regra de sustentabilidade)."],
    reconhecimento: ["Troféu elite.", "Marco: R$ 10.000.000 transacionados."],
    papel: [
      "Liderança macro da operação",
      "Expansão em larga escala",
      "Formação de novos líderes",
      "Consolidação da rede",
    ],
  },
];

/** Cargo do associado conforme `associateCareerRole` da sessão (percentuais no core). */
export function labelForAssociateCareerRole(role: string | null | undefined): string {
  switch (role) {
    case "ASSOCIATE":
      return CAREER_LEVELS[0].title;
    case "ASSOCIATE_MANAGER":
      return CAREER_LEVELS[1].title;
    case "EXPANSION_MANAGER":
      return CAREER_LEVELS[2].title;
    case "EXPANSION_DIRECTOR":
      return CAREER_LEVELS[3].title;
    default:
      return CAREER_LEVELS[0].title;
  }
}

export type NextStepMetric = {
  id: string;
  label: string;
  current: number;
  target: number;
  unit: "count" | "currency";
};

export type CareerState = {
  currentRank: CareerRank;
  currentLevel: CareerLevelDefinition;
  isMaxLevel: boolean;
  /** Metas do próximo nível ainda não cumpridas */
  nextSteps: NextStepMetric[];
  nextRank: CareerRank | null;
  nextLevelTitle: string | null;
};

function pct(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((current / target) * 1000) / 10);
}

/** Empresas (parceiros) premium consideradas no plano de carreira. */
function careerPartnerCount(d: DashboardType): number {
  return d.totalPremiumPartners ?? 0;
}

/** Associados premium considerados no plano de carreira. */
function careerAssociateCount(d: DashboardType): number {
  return d.totalPremiumAssociates ?? 0;
}

/**
 * Maior título de carreira atingido com base no dashboard atual.
 * Usa: empresas = totalPremiumPartners, associados = totalPremiumAssociates, faturamento = invoicing (período filtrado).
 */
export function computeCurrentRank(d: DashboardType): CareerRank {
  const p = careerPartnerCount(d);
  const a = careerAssociateCount(d);
  const inv = d.invoicing || 0;
  const t = CAREER_THRESHOLDS;

  if (p >= t.nivel3Empresas && inv >= t.marcoDiretorFaturamento) return 4;
  if (p >= t.nivel3Empresas && inv >= t.marcoGerenteFaturamento) return 3;
  if (a >= t.nivel2Associados && p >= t.nivel2EmpresasRede) return 2;
  return 1;
}

export function getLevelByRank(rank: CareerRank): CareerLevelDefinition {
  return CAREER_LEVELS.find((l) => l.rank === rank) ?? CAREER_LEVELS[0];
}

/** Próximas metas numéricas até o próximo degrau de título */
export function computeNextSteps(d: DashboardType, currentRank: CareerRank): NextStepMetric[] {
  const p = careerPartnerCount(d);
  const a = careerAssociateCount(d);
  const inv = d.invoicing || 0;
  const t = CAREER_THRESHOLDS;
  const steps: NextStepMetric[] = [];

  if (currentRank >= 4) return steps;

  if (currentRank === 1) {
    if (p < t.nivel1Empresas) {
      steps.push({
        id: "n1-empresas",
        label: "Empresas premium cadastradas (meta Nível 1)",
        current: p,
        target: t.nivel1Empresas,
        unit: "count",
      });
    }
    if (p >= t.nivel1Empresas) {
      if (a < t.nivel2Associados) {
        steps.push({
          id: "n2-associados",
          label: "Associados premium na equipe (Gestor)",
          current: a,
          target: t.nivel2Associados,
          unit: "count",
        });
      }
      if (p < t.nivel2EmpresasRede) {
        steps.push({
          id: "n2-empresas",
          label: "Empresas premium na rede (Gestor)",
          current: p,
          target: t.nivel2EmpresasRede,
          unit: "count",
        });
      }
    }
    return steps;
  }

  if (currentRank === 2) {
    if (p < t.nivel3Empresas) {
      steps.push({
        id: "n3-empresas",
        label: "Empresas premium ativas (Gerente)",
        current: p,
        target: t.nivel3Empresas,
        unit: "count",
      });
    }
    if (inv < t.marcoGerenteFaturamento) {
      steps.push({
        id: "n3-faturamento",
        label: "Volume transacionado — marco Gerente",
        current: inv,
        target: t.marcoGerenteFaturamento,
        unit: "currency",
      });
    }
    return steps;
  }

  if (currentRank === 3) {
    if (inv < t.marcoDiretorFaturamento) {
      steps.push({
        id: "n4-faturamento",
        label: "Volume transacionado — marco Diretor",
        current: inv,
        target: t.marcoDiretorFaturamento,
        unit: "currency",
      });
    }
    if (p < t.nivel3Empresas) {
      steps.push({
        id: "n4-empresas",
        label: "Empresas premium ativas na rede",
        current: p,
        target: t.nivel3Empresas,
        unit: "count",
      });
    }
    return steps;
  }

  return steps;
}

export function computeCareerState(d: DashboardType): CareerState {
  const currentRank = computeCurrentRank(d);
  const currentLevel = getLevelByRank(currentRank);
  const isMaxLevel = currentRank >= 4;
  const nextRank = isMaxLevel ? null : ((currentRank + 1) as CareerRank);
  const nextLevelTitle = nextRank ? getLevelByRank(nextRank).title : null;
  const nextSteps = computeNextSteps(d, currentRank);

  return {
    currentRank,
    currentLevel,
    isMaxLevel,
    nextSteps,
    nextRank,
    nextLevelTitle,
  };
}

export function formatStepValue(unit: "count" | "currency", value: number): string {
  if (unit === "count") return String(Math.floor(value));
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

export function overallNextProgressPercent(steps: NextStepMetric[]): number {
  if (steps.length === 0) return 100;
  const parts = steps.map((s) => pct(s.current, s.target));
  return Math.round((parts.reduce((a, b) => a + b, 0) / parts.length) * 10) / 10;
}

/** Métricas do dashboard para o resumo “o que você já conquistou”. */
export type DashboardSnapshot = {
  empresas: number;
  associados: number;
  faturamento: number;
};

export function getDashboardSnapshot(d: DashboardType): DashboardSnapshot {
  return {
    empresas: Math.floor(careerPartnerCount(d)),
    associados: Math.floor(careerAssociateCount(d)),
    faturamento: d.invoicing || 0,
  };
}

export type LevelProgressRow = {
  label: string;
  current: number;
  target: number;
  unit: "count" | "currency";
};

/** Linhas de progresso exibidas em cada nível (metas e marcos do documento). */
export function getProgressRowsForLevel(d: DashboardType, levelRank: CareerRank): LevelProgressRow[] {
  const p = careerPartnerCount(d);
  const a = careerAssociateCount(d);
  const inv = d.invoicing || 0;
  const t = CAREER_THRESHOLDS;

  switch (levelRank) {
    case 1:
      return [
        { label: "Empresas premium cadastradas (meta do nível)", current: p, target: t.nivel1Empresas, unit: "count" },
        {
          label: "Volume transacionado (marco plaquinha)",
          current: inv,
          target: t.marcoAssociadoFaturamento,
          unit: "currency",
        },
      ];
    case 2:
      return [
        { label: "Associados premium na equipe", current: a, target: t.nivel2Associados, unit: "count" },
        { label: "Empresas premium na rede", current: p, target: t.nivel2EmpresasRede, unit: "count" },
        {
          label: "Volume transacionado (marco liderança)",
          current: inv,
          target: t.marcoGestorFaturamento,
          unit: "currency",
        },
      ];
    case 3:
      return [
        { label: "Empresas premium ativas na rede", current: p, target: t.nivel3Empresas, unit: "count" },
        {
          label: "Volume transacionado (marco Gerente)",
          current: inv,
          target: t.marcoGerenteFaturamento,
          unit: "currency",
        },
      ];
    case 4:
      return [
        { label: "Empresas premium ativas na rede (mín. 1.000)", current: p, target: t.nivel3Empresas, unit: "count" },
        {
          label: "Volume transacionado (marco Diretor)",
          current: inv,
          target: t.marcoDiretorFaturamento,
          unit: "currency",
        },
      ];
    default:
      return [];
  }
}

export type LevelUiStatus = "conquistado" | "atual" | "proximo" | "futuro";

export function getLevelUiStatus(levelRank: CareerRank, currentRank: CareerRank): LevelUiStatus {
  if (levelRank < currentRank) return "conquistado";
  if (levelRank === currentRank) return "atual";
  if (levelRank === currentRank + 1) return "proximo";
  return "futuro";
}
